import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const COMPANY_INFO = {
  name: "warehouse",
  url: "https://www.visotonics.com/",
  phone: "+91-9651891556",
  address:
    "Plot No-11, KH. No.-501SA-502SA, Shukla Vihar, Devpur, Rajajipuram, Lucknow, Uttar Pradesh-226017",
};

const BLUE = [15, 42, 80];
const GRAY_LABEL = [130, 130, 130];
const DARK_TEXT = [20, 20, 20];

const formatDateTime = (t) =>
  t
    ? new Date(t).toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "-";

const getDuration = (start, end) => {
  if (!start || !end) return "-";
  const ms = new Date(end) - new Date(start);
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const drawHeader = (doc, pageWidth) => {
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, pageWidth, 4, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...DARK_TEXT);
  doc.text(COMPANY_INFO.name, 15, 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_LABEL);
  doc.text(COMPANY_INFO.url, pageWidth - 15, 15, { align: "right" });
  doc.text(COMPANY_INFO.phone, pageWidth - 15, 20, { align: "right" });

  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.6);
  doc.line(15, 27, pageWidth - 15, 27);
};

const drawFooter = (doc, pageWidth, pageHeight, pageNum) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_LABEL);
  doc.text(COMPANY_INFO.address, pageWidth / 2, pageHeight - 10, { align: "center" });
  doc.setTextColor(...DARK_TEXT);
  doc.text(String(pageNum).padStart(2, "0"), pageWidth - 15, pageHeight - 10, { align: "right" });
};

const drawFieldRow = (doc, y, left, right) => {
  const colWidth = 90;
  const x1 = 15;
  const x2 = 15 + colWidth;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_LABEL);
  doc.text(left.label.toUpperCase(), x1, y);
  doc.text(right.label.toUpperCase(), x2, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...DARK_TEXT);
  doc.text(String(left.value ?? "-"), x1, y + 6);
  doc.text(String(right.value ?? "-"), x2, y + 6);

  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.2);
  doc.line(x1, y + 11, 195, y + 11);
};

export const generateSessionReportPDF = async (session, skuDetails) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ── PAGE 1: Session fields + Cargo Summary ──────────────────────────
  drawHeader(doc, pageWidth);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...DARK_TEXT);
  doc.text("Session Report", pageWidth / 2, 38, { align: "center" });

  let y = 52;
  const rows = [
    [
      { label: "Session ID", value: `#${session.session_id}` },
      { label: "Truck Number", value: session.truck_number },
    ],
    [
      { label: "Gate", value: session.gate_name },
      { label: "Warehouse", value: session.warehouse_name || "-" },
    ],
    [
      { label: "Camera", value: session.camera_name || "-" },
      { label: "Activity", value: session.task },
    ],
    [
      { label: "Entry Time", value: formatDateTime(session.start_time) },
      { label: "Exit Time", value: formatDateTime(session.end_time) },
    ],
    [
      { label: "Duration", value: getDuration(session.start_time, session.end_time) },
      { label: "Cargo Count", value: session.cargo_count },
    ],
    [
      { label: "Status", value: (session.status || "-").toUpperCase() },
      { label: "Company", value: session.company_name || "-" },
    ],
  ];

  rows.forEach(([left, right]) => {
    drawFieldRow(doc, y, left, right);
    y += 16;
  });

  y += 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...BLUE);
  doc.text("Cargo Summary", 15, y);
  y += 4;

  autoTable(doc, {
    startY: y,
    head: [["Total Items", "Item Loaded", "Total SKU Count"]],
    body: [
      [
        session.cargo_count ?? 0,
        session.cargo_count ?? 0,
        skuDetails.length,
      ],
    ],
    theme: "grid",
    headStyles: { fillColor: BLUE, textColor: 255, fontStyle: "bold", halign: "center" },
    bodyStyles: { halign: "center", fontStyle: "bold", fontSize: 12 },
    styles: { cellPadding: 4 },
  });

  drawFooter(doc, pageWidth, pageHeight, 1);

  // ── PAGE 2: SKU / Goods Identification — all on one page, stacked vertically ──
  if (skuDetails.length > 0) {
    doc.addPage();
    drawHeader(doc, pageWidth);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...DARK_TEXT);
    doc.text("SKU / Goods Identification", pageWidth / 2, 38, { align: "center" });

    const margin = 15;
    const gap = 6;
    const boxWidth = pageWidth - margin * 2;

    const startY = 48;
    const availableHeight = pageHeight - startY - 20;
    const blockHeight = Math.min(
      75,
      (availableHeight - gap * (skuDetails.length - 1)) / skuDetails.length,
    );
    const imgHeight = Math.max(20, blockHeight - 16);

    let skuY = startY;

    for (let i = 0; i < skuDetails.length; i++) {
      const sku = skuDetails[i];

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...BLUE);
      doc.text(`SKU IMAGE ${i + 1}`, margin, skuY);

      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(margin, skuY + 1.5, margin + boxWidth, skuY + 1.5);

      const imgTop = skuY + 5;

      doc.setFillColor(30, 33, 41);
      doc.rect(margin, imgTop, boxWidth, imgHeight, "F");

      const dataUrl = sku.image_path ? await loadImageAsDataUrl(sku.image_path) : null;

      if (dataUrl) {
        const pad = 3;
        doc.addImage(
          dataUrl,
          "JPEG",
          margin + pad,
          imgTop + pad,
          boxWidth - pad * 2,
          imgHeight - pad * 2,
        );
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(220, 220, 220);
        doc.text("NO IMAGE AVAILABLE", margin + 4, imgTop + 8);
      }

      const labelBarY = imgTop + imgHeight + 2;
      const labelBarHeight = 9;

      doc.setFillColor(240, 244, 248);
      doc.rect(margin, labelBarY, boxWidth, labelBarHeight, "F");
      doc.setFillColor(...BLUE);
      doc.rect(margin, labelBarY, 1.5, labelBarHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(...DARK_TEXT);
      doc.text(sku.sku_name, margin + 6, labelBarY + 6.5);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(...BLUE);
      doc.text(`Count: ${sku.count}`, margin + boxWidth - 5, labelBarY + 6.5, { align: "right" });

      skuY = labelBarY + labelBarHeight + gap;
    }

    drawFooter(doc, pageWidth, pageHeight, doc.internal.getNumberOfPages());
  }

  doc.save(`session_report_${session.session_id}.pdf`);
};